:host {
  color-scheme: light dark;

  --backdrop: rgba(255, 255, 255, 0.80);
  --blue-1: #0056d2;
  --gray-12: #F8F8F8;
  --gray-13: #e6f0ff;
  --neutral-100: #F4F4F5;
  --pill-background: var(--gray-12);
  --pill-background-active: var(--gray-13);
  --disabled-border: var(--gray-4);
  --disabled-background: var(--gray-1);
  --success-color: #479f4e;
}

@media (prefers-color-scheme: dark) {
  :host {
    --backdrop: rgba(28, 34, 44, 0.80);
    --pill-background: var(--primary-black);
    --pill-background-active: var(--neutral-100);
  }
}

:host {
  display: none;
  pointer-events: none;
  z-index: 99999;
}

:host(.open) {
  display: contents;
  position: absolute;
  pointer-events: auto;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--backdrop);
    overflow-y: auto;
  }
}

:host(.open) .backdrop-overlay {
  opacity: 1;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--backdrop);
  z-index: -1;
  backdrop-filter: blur(6px);
}

:host(.open) .backdrop-overlay.no-blur {
  backdrop-filter: none;
}

/**
 * Container Styles
 */
.contact-container {
  position: relative;
  background-color: var(--background);
  padding: 32px;
  pointer-events: auto;

  @media (max-width: 768px) {
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 100%;
    box-shadow: none;
  }
}

:host(.tooltip) .contact-container {
  width: 312px;
  max-width: 90vw;
  box-shadow:
    0px 4px 16px rgba(0, 0, 0, 0.15),
    0px 0px 0px 1px rgba(0, 0, 0, 0.05);
  z-index: 10;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    box-shadow: none;
    min-height: auto;
  }
}

/**
 * Tooltip Caret Style
 */
:host(.tooltip) .contact-container::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 5%;
  transform: translateX(-5%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--background);
  filter: drop-shadow(0px -2px 4px rgba(0, 0, 0, 0.1));

  @media (max-width: 768px) {
    display: none;
  }
}

.contact-container .close-button {
  position: absolute;
  top: 28px;
  right: 24px;
  background-color: transparent;
  color: var(--text-body);
  border: none;
  padding: 10px;
  cursor: pointer;

  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    padding: 12px;
    z-index: 1;
  }
}

.contact-container .contact-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/**
 * Form Styles
 */
.form-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.form-header .title {
  font-style: normal;
  font-weight: 600;
  line-height: 110%;
  font-size: 20px;
  text-align: center;
  margin: 0;
}

.form-header .subhead {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  color: var(--text-hint);
  margin: 4px auto 0;
  text-align: center;
}

/* Form Body */
.form-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-hint);
  margin: 0;
}

/* Discipline Selection */
.discipline-options {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.discipline-option {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 16px;
  height: 29px;
  border: 1px solid var(--border);
  background-color: var(--pill-background);
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--body-font);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-body);
  text-align: center;
}

.discipline-option:hover {
  border-color: var(--text-links);
  color: var(--text-links);
}

.discipline-option.selected {
  border-color: var(--primary);
  color: var(--primary);
}

/* Form Groups */
.form-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 64px;
  gap: 8px;
  width: 100%;
}

.form-group label {
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  color: var(--text-hint);
}

.form-group input,
.form-group textarea {
  display: block;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border: 1px solid var(--border);
  appearance: none;
  color: var(--text-body);
  resize: vertical;
  max-width: 100%;
  padding: 12px;
  outline: 0;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: var(--body-font);
  background-color: var(--input-bg);
}

.form-group input:hover,
.form-group textarea:hover {
  border-color: var(--border-input);
}

.form-group textarea {
  align-items: flex-start;
  min-height: 105px;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: var(--text-hint);
}

/* Form errors */
.form-group.has-error input,
.form-group.has-error textarea {
  border: 1px solid var(--red);
}

.form-group.has-error .error-message,
.form-section .error-message {
  color: var(--red);
  font-size: 12px;
  margin: 2px 0 0 0;
}

/* Hide empty error messages to prevent unnecessary spacing */
.error-message:empty {
  display: none;
  margin: 0;
}

.form-section .error-message {
  margin-top: -8px;
}

/* Submit Error */
.submit-error {
  background-color: #fee;
  border: 1px solid var(--red);
  border-radius: 4px;
  padding: 12px;
  color: var(--red);
  font-size: 14px;
  margin-bottom: 16px;
  text-align: center;
}

/* Form Footer */
.form-footer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.submit-button {
  padding: 10px 24px;
  min-width: 100%;
  text-align: center;
  border-radius: 2px;
  border: 1px solid var(--primary);
  cursor: pointer;
  line-height: normal;
  font-family: var(--body-font);
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  white-space: nowrap;
  background-color: var(--primary);
  color: white;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--blue-1);
  border-color: var(--blue-1);
}

.submit-button:disabled {
  background-color: var(--disabled-background);
  border-color: var(--disabled-border);
  color: var(--text-hint);
  cursor: not-allowed;
}

.disclaimer {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: var(--text-hint);
  margin: 0;
  text-align: center;
  max-width: 400px;
}

/* Alternative Contact */
.alternative-contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
}

.divider {
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
}

.divider::before, .divider::after {
  flex: 1;
  content: '';
  padding: 0.5px;
  background-color: var(--border);
  margin: 5px;
}

.website-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-links);
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}

.website-link:hover {
  color: var(--primary);
}

.reset-button {
  padding: 10px 24px;
  min-width: 180px;
  text-align: center;
  border-radius: 2px;
  border: 1px solid var(--primary);
  cursor: pointer;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  font-size: 16px;
  white-space: nowrap;
  background-color: var(--primary);
  color: white;
}

.reset-button:hover {
  background-color: var(--blue-1);
  border-color: var(--blue-1);
}

.close-button-text {
  background: none;
  border: none;
  color: var(--text-links);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 16px;
}

.close-button-text:hover {
  color: var(--primary);
}

/* Inline Success Message */
.success-message {
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: #ebf7ed;
  border-left: 4px solid var(--success-color);
  padding: 16px;
}

.success-icon {
  color: var(--success-color);
  flex-shrink: 0;
}

.success-content h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1f2937;
}

.success-content p {
  font-size: 14px;
  margin: 0;
  color: #6b7280;
}

/* Disabled form state */
.form-section.disabled,
.form-group.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.form-section.disabled .discipline-option,
.form-group.disabled input,
.form-group.disabled textarea {
  background-color: var(--disabled-background);
  cursor: not-allowed;
}